<!--
 * @Description: 名片管理
 * @Author: mdb
 * @Date: 2023-04-11 17:57:45
 * @LastEditTime: 2023-05-05 14:26:02
 * @LastEditors: mdb
-->
<template>
  <dc-layout-main>
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        ref="tableRef"
        tableId="DcCosZbuCardManage"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item name="follower" label="销售员">
              <UserSelect v-model:value="queryForm.follower" placeholder="请选择" allowClear />
            </dc-form-item>
            <dc-form-item name="startTime" label="创建时间">
              <CosRangePicker v-model:startTime="queryForm.createStartTime" v-model:endTime="queryForm.createEndTime" />
            </dc-form-item>
            <dc-form-item name="createdBy" label="创建人">
              <UserSelect v-model:value="queryForm.createdBy" placeholder="请选择" allowClear />
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <a-button v-permission="permission.add" type="primary" @click="handleChange()">新增</a-button>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'action'">
            <MoreButton>
              <a-button v-permission="permission.qrcode" type="link" @click="showModal('zh-CN', record)"
                >中文二维码</a-button
              >
              <a-button v-permission="permission.qrcode" type="link" @click="showModal('en-US', record)"
                >英文二维码</a-button
              >
              <a-button v-permission="permission.edit" type="link" @click="handleChange(record)">编辑</a-button>
              <a-button v-permission="permission.del" type="link" @click="handleDelete(record)">删除</a-button>
            </MoreButton>
          </template>
        </template>
      </cos-table>

      <a-modal v-model:visible="visible" :title="qrCodeTitle" @ok="handleOk">
        <template #footer>
          <a-button key="submit" type="primary" @click="handleCopy">复制链接</a-button>
          <a-button key="submit" type="primary" @click="handleOk">下载二维码</a-button>
        </template>
        <vue-qr
          :margin="0"
          :size="400"
          :text="qrCodeUrl"
          :logoSrc="qrCodeLogoEnum[props.subdomain]"
          :logoScale="0.25"
          :logoMargin="6"
          :correctLevel="0"
          ref="qrCodeRef"
        />
      </a-modal>
    </dc-layout-content>
  </dc-layout-main>

  <!-- 新增/编辑销售名片弹窗 -->
  <ModalForm ref="modalFormRef" @fetch="handleQueryChange('fetch')" />
</template>

<script setup lang="ts">
import { reactive, computed, ref, unref, PropType } from 'vue'
import vueQr from 'vue-qr/src/packages/vue-qr.vue'
import { message } from '@dc-platform/dc-components'
import UserSelect from '@/components/UserSelect/index'
import CosRangePicker from '@/components/CosRangePicker/index'
import ModalForm from './components/ModalForm.vue'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { downloadFileByBase64 } from '@/utils/index'
import { businessCardList, businessCardDel } from '@/api/marketing/marketingManage'
import Modal from '@/hooks/modalType'
import type { BusinessCardParams, BusinessCardListItem } from '@/typings/api/marketingManage'
const { clipboardRef, copiedRef } = useCopyToClipboard()

const props = defineProps({
  subdomain: {
    type: String as PropType<'fbu' | 'lbu'>,
    required: true
  },
  permission: {
    type: Object as PropType<{
      add: string
      edit: string
      del: string
      qrcode: string
    }>,
    default: () => ({}),
    required: true
  }
})

const tableRef = ref()
const queryForm = reactive<BusinessCardParams>({})

const columns = computed(() => [
  { title: '销售员', dataIndex: 'followerName' },
  { title: '当前关联活动', dataIndex: 'activityName' },
  { title: '创建人', dataIndex: 'createByName' },
  { title: '创建时间', dataIndex: 'createTime', width: 190 },
  { title: '操作', dataIndex: 'action', width: 240, fixed: 'right' }
])
const getList = (queryForm: BusinessCardParams, callBack: (res: any) => void) => {
  businessCardList(queryForm).then((res) => {
    callBack(res)
  })
}
const handleQueryChange = (type: 'search' | 'fetch') => {
  if (type === 'search') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}

// 新增/编辑员工时区弹窗
const modalFormRef = ref()
const handleChange = (rows?: BusinessCardListItem) => {
  modalFormRef.value?.init(rows)
}
const handleDelete = (rows: BusinessCardListItem) => {
  Modal.delete({
    content: '删除操作无法恢复，请谨慎操作',
    onOk() {
      businessCardDel({ id: rows.id }).then(() => {
        message.success('删除成功')
        handleQueryChange('fetch')
      })
    }
  })
}

const qrCodeRef = ref()
const qrCodeTitle = ref('')
const visible = ref<boolean>(false)
const currentRow = ref<BusinessCardListItem>()
const qrCodeType = ref<'zh-CN' | 'en-US'>()
const qrCodeUrl = ref('')
const qrCodeLogoEnum = {
  fbu: require('@/assets/logo_gucang.png'),
  lbu: require('@/assets/logo_yuntu.png')
}
const handleCopy = () => {
  clipboardRef.value = qrCodeUrl.value
  if (unref(copiedRef)) {
    message.success('复制成功')
  } else {
    message.error('该浏览器不支持自动复制!')
  }
}
const showModal = (lang: 'zh-CN' | 'en-US', row: any) => {
  const langEnum = { 'zh-CN': '中文二维码', 'en-US': '英文二维码' }
  qrCodeType.value = lang
  qrCodeTitle.value = langEnum[lang]
  currentRow.value = row
  qrCodeUrl.value = `${window.WEB_URL}/mobile.html#/businessCard?cardId=${currentRow.value?.id}&follower=${currentRow.value?.follower}&subdomain=${props.subdomain}&lang=${qrCodeType.value}`
  visible.value = true
}
const handleOk = () => {
  downloadFileByBase64(qrCodeRef.value.imgUrl, `${currentRow.value?.followerName}-${qrCodeType.value}`)
  visible.value = false
}
</script>
<script lang="ts">
export default { name: 'DcCosZbuCardManage' }
</script>

<style scoped></style>
